import React, { useEffect, useState } from 'react';
import OlcButton from '~/components/General/OlcButton';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { getAllTransactions } from '~/redux/actions/stripe-actions';

// Utils
import { dateFormat } from '~/utils/date-format';
import { balanceFormatter } from '~/utils/helperFunctions';
import { TRANSACTION_STATUS, DEFAULT_PAGE_SIZES } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';

// Components
import NoRecords from '~/components/General/NoRecords';
import TruncatedText from '~/components/General/TruncatedText';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import OlcBulkSelectionDropdown from '~/components/General/OlcBulkSelectionDropdown';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Box,
  Typography,
  Stack,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';

// Icons
import {
  CheckedCIcon,
  ExportPaymentIcon,
  NoTransactionsIcon,
  RemovedCheckedCIcon,
  UncheckedCIcon,
} from '~/assets/images';

// Styles
import './styles.scss';

const TransactionListing = ({ setSelectedRows, selectedRows, onExportClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [bulkSelect, setBulkSelect] = useState(false);
  const [removeBulkSelect, setRemoveBulkSelect] = useState(false);
  const [rows, setRows] = useState([]);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [showNoRows, setShowNoRows] = useState(false);
  const dispatch = useDispatch();

  const fetchedRows = useSelector((state) => state.stripeReducers?.transactions.rows);

  const transactions = useSelector((state) => state.stripeReducers?.transactions) || {};

  const isLoading = useSelector((state) => state.stripeReducers?.transactions?.loading);

  const pageSizes = DEFAULT_PAGE_SIZES;

  // Get current page size with fallback
  const currentPageSize = transactions.perPage || 20;

  const open = Boolean(anchorEl);

  // Ensure page scroll is not hidden when dropdown menu is open

  const selectAll = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((trans) => ({ ...trans, checked: true }));
    setRows(updatedRows);
    setSelectedRows([-1]);
  };

  const selectVisble = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((trans) => ({ ...trans, checked: true }));
    setRows(updatedRows);
    const selectedRowIds = updatedRows.map((trans) => trans.id);
    setSelectedRows(selectedRowIds);
  };

  const selectNone = () => {
    setBulkSelect(false);
    setRemoveBulkSelect(false);
    const updatedRows = rows.map((trans) => ({ ...trans, checked: false }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const selectRow = (event, selectedRow) => {
    event.stopPropagation();
    // Update the selectedRow object directly in the rows array

    const updatedRows = rows.map((row) =>
      row.id === selectedRow.id ? { ...row, checked: !row.checked } : row
    );
    // If the user selects all trans and then removes a single trans, update the selected rows array accordingly.
    if (selectedRows[0] === -1) {
      const filteredRows = rows.map((row) =>
        row.id === selectedRow.id ? { ...row, checked: !row.checked } : { ...row, checked: true }
      );
      const filteredRowsIds = filteredRows
        .map((trans) => trans.checked === true && trans.id)
        .filter(Boolean);
      setSelectedRows(filteredRowsIds);
      setRows(updatedRows);
      setRemoveBulkSelect(true);
      return;
    }

    setRows(updatedRows);

    const selectedRowIds = selectedRows.includes(selectedRow.id)
      ? selectedRows.filter((id) => id !== selectedRow.id) // Remove the ID if already selected
      : [...selectedRows, selectedRow.id]; // Add the ID if not selected
    // Update the selectedRows state with the new array
    setSelectedRows(selectedRowIds);
    setRemoveBulkSelect(true);
  };

  const bulkSelection = () => {
    const newBulkSelect = !bulkSelect;
    setBulkSelect(newBulkSelect);
    const updatedRows = rows.map((trans) => ({
      ...trans,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);
    const selectedRowIds = updatedRows.map((trans) => trans.id);
    newBulkSelect ? setSelectedRows(selectedRowIds) : setSelectedRows([]);
  };

  const removeBulkSelection = () => {
    const newBulkSelect = !removeBulkSelect;
    setRemoveBulkSelect(newBulkSelect);
    setBulkSelect(newBulkSelect);
    const updatedRows = rows.map((trans) => ({
      ...trans,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  useEffect(() => {
    setSelectedRows((currentSelection) => (currentSelection[0] === -1 ? currentSelection : []));
  }, [transactions.currentPage, transactions.perPage]);

  useEffect(() => {
    !selectedRows.length && setRemoveBulkSelect(false);
    (rows.length && selectedRows.length === rows.length) || selectedRows[0] === -1
      ? (setBulkSelect(true), setRemoveBulkSelect(false))
      : setBulkSelect(false);
  }, [selectedRows, rows]);

  useEffect(() => {
    setRows(
      (fetchedRows || []).map((transaction) => ({
        ...transaction,
        checked: selectedRows[0] === -1 || selectedRows.includes(transaction.id),
      }))
    );
  }, [fetchedRows, selectedRows]);

  const columns = [
    {
      field: ' ',
      headerClassName: 'olcBulkSelectHeader',
      cellClassName: 'olcBulkSelectCell',
      renderHeader: () => (
        <Box className="checkboxHeader">
          {removeBulkSelect ? (
            <Checkbox
              className="bulkCheck"
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={RemovedCheckedCIcon} alt="removed checked" />}
              checked={removeBulkSelect}
              onClick={removeBulkSelection}
            />
          ) : (
            <Checkbox
              className="bulkCheck"
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
              checked={bulkSelect}
              onClick={bulkSelection}
            />
          )}
          <OlcBulkSelectionDropdown
            ariaLabel="Open transaction selection options"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            open={open}
          />
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={() => setAnchorEl(null)}
            onClick={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            classes={{
              paper: 'menuOptionsPaper',
              list: 'menuOptionsList',
            }}
            sx={{ marginTop: '7px' }}
          >
            <MenuItem
              onClick={transactions.count > 0 ? selectAll : undefined}
              className={selectedRows[0] === -1 ? 'highlight' : ''}
            >
              {MESSAGES.ACCOUNT.WALLET.EXPORT.SELECT_ALL_ITEMS} ({transactions.count})
            </MenuItem>
            <MenuItem onClick={selectVisble}>
              {MESSAGES.ACCOUNT.WALLET.EXPORT.SELECT_VISIBLE_ITEMS}
            </MenuItem>
            <MenuItem onClick={selectNone}>{MESSAGES.ACCOUNT.WALLET.EXPORT.SELECT_NONE}</MenuItem>
          </Menu>
        </Box>
      ),
      width: 75,
      sortable: false,
      renderCell: (params) => (
        <Box className="checkboxRow">
          <Checkbox
            onClick={(event) => selectRow(event, params.row)}
            className="bulkCheck"
            icon={<img src={UncheckedCIcon} alt="unchecked" />}
            checkedIcon={<img src={CheckedCIcon} alt="checked" />}
            checked={params.row.checked}
          />
        </Box>
      ),
    },
    {
      field: 'date',
      headerName: 'Date',
      headerClassName: 'olcBulkSelectAdjacentHeader',
      width: 200,
      sortable: false,
      flex: window.innerWidth >= 1350 ? 1 : 0,
      renderCell: (params) => <Typography>{dateFormat(params.row.createdAt, true)}</Typography>,
    },
    {
      field: 'transactionId',
      headerName: 'Transaction ID',
      width: 200,
      sortable: false,
      flex: window.innerWidth >= 1350 ? 1 : 0,
      renderCell: (params) => <Typography>{params.row.id}</Typography>,
    },
    {
      field: 'description',
      headerName: 'Description',
      type: 'number',
      width: 500,
      sortable: false,
      flex: window.innerWidth >= 1350 ? 2 : 0,
      cellClassName: 'walletDescriptionCell',
      renderCell: (params) => (
        <Box className="descriptionWrapper">
          <TruncatedText text={params.row.remarks} />
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 200,
      height: 550,
      sortable: false,
      flex: window.innerWidth >= 1350 ? 1 : 0,
      renderCell: (params) => {
        const balance = balanceFormatter(params.row.amount);
        return (
          <Typography
            className={
              params.row.type === 'Debited' ? 'textErrorTransaction' : 'textSuccessTransaction'
            }
          >
            <span className={params.row.type === 'Debited' ? 'icon-color' : 'icon-color-success'}>
              {TRANSACTION_STATUS[params.row.type]}
            </span>{' '}
            {balance}
          </Typography>
        );
      },
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 200,
      sortable: false,
      flex: window.innerWidth >= 1350 ? 1 : 0,
      renderCell: (params) => {
        const balance = balanceFormatter(params.row.meta.newBalance);
        return <Typography>{balance}</Typography>;
      },
    },
  ];

  useEffect(() => {
    handleGetTransactions(transactions.currentPage || 1, currentPageSize);
  }, []);
  const handleGetTransactions = (page, perPage) => {
    dispatch(getAllTransactions(page, perPage));
  };

  const handleChangePage = (event, page) => {
    handleGetTransactions(page, currentPageSize);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    handleGetTransactions(1, newPageSize); // Reset to page 1 when changing page size
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Mirror CustomTable behavior: delay showing NoRecords until loading completes
  useEffect(() => {
    let timeoutId;
    if (!isLoading && (rows?.length || 0) === 0) {
      timeoutId = setTimeout(() => setShowNoRows(true), 300);
    } else {
      setShowNoRows(false);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading, rows]);

  return (
    <Box>
      <Box className="exportBtnWrapper">
        <OlcButton
          variant="secondary"
          className="exportTransBtn"
          onClick={onExportClick}
          disabled={selectedRows.length === 0}
        >
          <img src={ExportPaymentIcon} style={{ width: '20px', height: '20px' }} />
          <Typography>{MESSAGES.ACCOUNT.WALLET.EXPORT.EXPORT_BTN_TEXT}</Typography>
        </OlcButton>
      </Box>
      <div
        style={{
          width: '100%',
        }}
        className="fundsTableWrapper"
      >
        <Box sx={{ position: 'relative' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            compactColumns={false}
            pageSizeOptions={[20, 50, 100]}
            paginationMode="server"
            hideFooterSelectedRowCount
            loading={isLoading}
            hideFooterPagination
            rowSelection={false}
            getRowId={(row) => row?.id}
            disableColumnMenu={true}
            className="fundsTableGrid"
            rowHeight={64}
            sx={{
              ...(isLoading ? { height: '126px' } : {}), // 61px header + 65px body when loading
            }}
            slots={{
              noRowsOverlay: () => null,
            }}
          />
        </Box>
      </div>
      {showNoRows && (
        <Box style={{ marginTop: '32px' }}>
          <NoRecords
            icon={
              <img
                src={NoTransactionsIcon}
                alt="NoContact"
                style={{ width: '40px', height: '40px' }}
              />
            }
            title="No Transactions to show"
            message={
              <>
                Transaction history lets you track Wallet activity <br /> and credit card payments
                all in one place.
              </>
            }
            minBoxHeight="auto"
          />
        </Box>
      )}
      {!isLoading && transactions.count > pageSizes[0] && (
        <OlcTablePagination
          count={Math.ceil(transactions.count / currentPageSize)}
          page={transactions.currentPage || 1}
          pageSize={currentPageSize}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={handlePageSizeChange}
          from={transactions.from}
          to={transactions.to}
          total={transactions.count}
        />
      )}
    </Box>
  );
};

export default TransactionListing;
